<!--layouts/Main-->
<template>
    <v-app>
        <AppNavigation v-model="selectedMenuItem"
                       :display-name="getDisplayName"
                       :menu="menu"
                       :homepage="homepage"
                       @open-hamburger="hamburgerOpen = true">
        </AppNavigation>

        <v-navigation-drawer v-model="hamburgerOpen"
                             app
                             temporary>
            <v-list nav
                    dense>
                <v-list-item-group active-class="primary--text">

                    <template v-for="m in menu">

                        <template v-if="!hasChildren(m)">
                            <v-list-item :key="m.title" :to="m.to" :href="m.href">
                                <v-list-item-icon class="justify-center">
                                    <v-icon>{{ m.icon }}</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>{{ m.title }}</v-list-item-title>
                            </v-list-item>
                        </template>

                        <template v-else>
                            <v-list-group :prepend-icon="m.icon" :key="m.title">
                                <template v-slot:activator>
                                    <v-list-item-title>{{ m.title }}</v-list-item-title>
                                </template>

                                <v-list-item v-for="c in m.children" :key="c.title" :to="c.to" :href="c.href">
                                    <v-list-item-icon>
                                        <v-icon>{{ c.icon }}</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-title>{{ c.title }}</v-list-item-title>
                                </v-list-item>
                            </v-list-group>
                        </template>

                    </template>
                </v-list-item-group>
            </v-list>
        </v-navigation-drawer>

        <v-main>
            <template v-if="selectedMenuItem && hasChildren(selectedMenuItem)">
                <v-toolbar color="primary" dark dense>
                    <v-tabs optional background-color="primary" show-arrows>
                        <v-tab v-for="m in selectedMenuItem.children" :to="m.to" :key="m.title">{{ m.title }}</v-tab>
                    </v-tabs>
                </v-toolbar>
            </template>
            <router-view></router-view>
        </v-main>

        <v-footer padless>
            <v-card class="text-center" width="100%" tile dark>
                <v-card-text class="d-flex justify-center"> 
                    <a href="https://www.aicpa.org/soc4so" target="_blank" style="text-decoration: none;">
                        <v-img src="/img/21972-312_SOC_NonCPA.png" max-height="150" max-width="150"></v-img>                        
                        <div class="mt-1 text-h6" style="color: rgb(188 188 188);">SOC 2 &reg; Type II</div>                        
                    </a>                    
                </v-card-text>
                <v-card-text>
                    <span><strong>Employers, Advisors, Plan Administrators, & Health Care Providers:</strong></span>
                    <br />
                    <span><a href="https://www.maxcarerx.com">Click here</a> for the MaxCare Rx Corporate site.</span>
                    <br /><br/>
                    <span><a href="https://www.ppok.com/PrivacyPolicy">Privacy Policy</a>&nbsp;&bull;&nbsp;<a href="#" @click.prevent="viewReleaseNotes">Release Notes</a></span>
                </v-card-text>
            </v-card>
        </v-footer>

        <BlockUi :darkMode="getDarkMode"
                 :show="getBlockUIShow"
                 :text="getBlockUIMessage"></BlockUi>

        <DialogAlert :messageType="getMessageType"
                     :message="getMessageMsg"
                     :show="getMessageShow"
                     @dialog-alert-closed="resetMessage"></DialogAlert>

        <v-dialog v-model="releaseNotes.show"
                  :max-width="450"
                  scrollable>
            <v-card>
                <v-card-title>Release Notes</v-card-title>

                <v-divider></v-divider>

                <v-card-text>
                    <template v-if="releaseNotes.infos[0]">
                        <div v-for="i in releaseNotes.infos" :key="i.releaseId">
                            <div class="text-h6">{{ i.releasedDate | formatDate }}</div>

                            <p>{{ i.releaseNotesClean }}</p>
                        </div>
                    </template>
                    <template v-else>
                        <p>No recent release notes.</p>
                    </template>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions>
                    <v-spacer></v-spacer>

                    <v-btn @click="releaseNotes.show = false">
                        Close
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-snackbar v-model="snackBarShow">
            {{ getSnackbarMessage }}

            <template v-slot:action="{ attrs }">
                <v-btn color="red"
                       text
                       v-bind="attrs"
                       @click="snackBarShow = false">
                    Close
                </v-btn>
            </template>
        </v-snackbar>
    </v-app>
</template>

<script>
    import AppNavigation from '@/components/app/AppNavigation'
	import BlockUi from '@/components/app/BlockUI'
	import DialogAlert from '@/components/app/DialogAlert'

	import AppMixin from '@/mixins/app'
	import BlockUiMixin from '@/mixins/blockUI'
	import MessageHandlerMixin from '@/mixins/messageHandler'
	import SnackbarMixin from '@/mixins/snackbar'
	import UserMixin from '@/mixins/user'
    import UserSettingsMixin from '@/mixins/userSettings'

    import { Api, RouteTitle, Url } from '@/utility/constants'

    const menu = [
        { title: "Home", to: Url.HOMEPAGE, icon: "fas fa-home" },
        { title: RouteTitle.HELPFUL_DOCUMENTS, to: Url.HELPFUL_DOCUMENTS, icon: "fas fa-file-alt" },
        { title: RouteTitle.WELLNESS, to: Url.WELLNESS, icon: "fas fa-heartbeat" },
        { title: RouteTitle.CONTACT_US, to: Url.CONTACT_US, icon: "fas fa-mobile-alt" },
        {
            title: RouteTitle.MY_BENEFITS, href: Url.MY_BENEFITS, icon: "fas fa-user-circle", isLogin: true,

            // This was for internal member portal management
            //children: [
            //    { title: RouteTitle.MY_BENEFITS_OVERVIEW, to: Url.MY_BENEFITS_OVERVIEW, icon: "fas fa-info-circle", hidePublic: true },
            //    { title: RouteTitle.DRUG_LISTS, to: Url.DRUG_LISTS, icon: "fas fa-list", hidePublic: true },
            //    // Until we get network stuff sorted, this should be hidden
            //    //{ title: RouteTitle.MY_PHARMACY_LOCATOR, to: Url.MY_PHARMACY_LOCATOR, icon: "fas fa-map-marked-alt", hidePublic: true },
            //    { title: RouteTitle.ID_CARD, to: Url.ID_CARD, icon: "fas fa-id-card", hidePublic: true }
            //]
        }
    ]

	export default {
		components: {
            AppNavigation,
			BlockUi,
			DialogAlert
		},

		mixins: [
			AppMixin,
			BlockUiMixin,
			MessageHandlerMixin,
			SnackbarMixin,
			UserMixin,
			UserSettingsMixin
        ],

        data() {
            return {
                menu: menu,
                selectedMenuItem: null,
                hamburgerOpen: false,
                releaseNotes: {
                    show: false,
                    infos: []
                }
            }
        },

		mounted() {
			this.initComponent()
		},

		computed: {
			snackBarShow: {
				get() {
					return this.getSnackbarShow
				},

				set(val) {
					this.setSnackbarShow(val)
				}
            },

            homepage() {
                return Url.HOMEPAGE
            },

            isLoggedIn() {
                return this.getUserId !== 0
            }
        },

        methods: {
            hasChildren(menuItem) {
                if (menuItem.children) {
                    let publicChildren = menuItem.children.filter(x => this.isLoggedIn || !x.hidePublic);
                    if (publicChildren[0]) return true;
                }
                return false;
            },

			initComponent() {
				this.showBlockUI()

				this.$http({
					url: Api.API.GET_APP_DATA_NO_AUTH,
					method: Api.METHOD.POST
				}).then(response => {
					let resp = response.data

					this.hideBlockUI()

					if (resp.success) {
						this.setRecaptchaPublicKey(resp.data.recaptchaPublicKey)
					} else {
						this.showMessage('error', resp.errorMessage)
					}
				}).catch(error => {
					this.hideBlockUI()
					this.showMessage('error', 'Unable to retrieve App Data at this time. ' + error)
				})
			},

            viewReleaseNotes() {
                this.$http({
                    url: Api.HOME.GET_RELEASE_INFO,
                    method: Api.METHOD.GET
                }).then(response => {
                    this.releaseNotes.infos = response.data
                    this.releaseNotes.show = true
                }).catch(error => {
                    if (error && error.response.status !== 401) {
                        this.showMessage('error', 'Unable to retrieve release notes at this time.')
                    }
                })
            }
        }
    }
</script>

<style>
	.v-sheet--offset {
		top: -24px;
		position: relative;
	}

	.cursor-pointer {
		cursor: pointer;
	}
</style>